/* Pointer + keyboard input: hit testing, drags, wall drawing, shortcuts. */
import { OPENING_DEFS } from './types.ts';
import type { OpeningType, Pt, Wall } from './types.ts';
import { $, allIds, findItem, hooks, keys, pushUndo, redo, state, undo } from './state.ts';
import { clamp, deg, fmtLen, rad } from './utils.ts';
import { distSide, distToSeg, nearestWall, segIntersectsRect, wallPt, wallVec } from './geom.ts';
import { snapPt, snapV, wallDraftEnd, wallEndSnap } from './snap.ts';
import { canvas, CSS_W, CSS_H, fitView, hideLenBox, requestDraw, setViewOpt, zoomAt } from './render.ts';
import { clampOpenings, collectWeld, dragWallEnd, type WeldMove } from './weld.ts';
import { inferDrawConstraints, inferDropConstraints, restoreWalls, snapshotWalls, solveConstraints } from './solver.ts';
import { applyConstrainClick, renderPanel } from './panel.ts';
import { editFixtureLabel, editLabel, editNewLabel, editConstraint, editWallDim, overDim, overTie } from './edit.ts';
import { addRoom, deleteSel, duplicateSel, flipDoor, rotateSel } from './ops.ts';
import { normRect, ROOM_TH, snapRoomCorner, squareDrag } from './room.ts';
import { snapFixtureToWall } from './fixsnap.ts';
import { setTool } from './tools.ts';
import { markDirty, markDirtySoft, reportCursor, saveNow } from './net.ts';
import { FIXTURES } from './fixtures.ts';
import { uid } from './utils.ts';

let lastPanelRefresh = 0;
/** Fingers down on the canvas (screen px by pointerId) — two of them pinch-zoom / pan. */
const touches = new Map<number, Pt>();
let lastPointerType = 'mouse';
/** Handle hit radius: fingers are fat. */
const handleR = () => lastPointerType === 'touch' ? 18 : 9;

function startPinch(): void {
  const [a, b] = [...touches.values()];
  // whatever one finger was doing is off: a second finger means "navigate"
  if (state.roomDraft) state.roomDraft = null;
  if (state.drag && state.drag.kind === 'measure') state.measure = null;
  const v = state.view;
  state.drag = { kind: 'pinch', d0: Math.hypot(b.x - a.x, b.y - a.y) || 1, mid0: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, zoom0: v.zoom, vx0: v.x, vy0: v.y };
  hideLenBox(); requestDraw();
}
function applyPinch(): void {
  const d = state.drag; if (!d || d.kind !== 'pinch' || touches.size < 2) return;
  const [a, b] = [...touches.values()];
  const dist = Math.hypot(b.x - a.x, b.y - a.y) || 1, mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const zoom = clamp(d.zoom0 * dist / d.d0, .25, 60);
  // the world point that was under the fingers' midpoint stays under it
  const wx = d.vx0 + d.mid0.x / d.zoom0, wy = d.vy0 + d.mid0.y / d.zoom0;
  state.view.zoom = zoom;
  state.view.x = wx - mid.x / zoom; state.view.y = wy - mid.y / zoom;
  $('#zoomPct').textContent = Math.round(zoom / 4 * 100) + '%';
  requestDraw();
}

/* Live diagnostics for the ?perf=1 overlay (negligible cost, always on). */
export const perf = {
  moveMs: 0, moves: 0, lastEvent: '—', lastEventAt: 0, cancels: 0,
  note: '',
};
const pev = (name: string) => { perf.lastEvent = name; perf.lastEventAt = performance.now(); };

function evtPos(e: { clientX: number; clientY: number }) {
  const r = canvas.getBoundingClientRect();
  const sx = e.clientX - r.left, sy = e.clientY - r.top;
  return { sp: { x: sx, y: sy }, p: { x: state.view.x + sx / state.view.zoom, y: state.view.y + sy / state.view.zoom } };
}

/* =============================== hit testing =============================== */
export function hitTest(p: Pt, sp: Pt): { kind: string; item: any } | null {
  const z = state.view.zoom;
  // labels (screen-space bbox); hidden labels aren't clickable
  if (state.show.labels) for (let i = state.doc.labels.length - 1; i >= 0; i--) {
    const l = state.doc.labels[i];
    if (l._bb && sp.x >= l._bb.x && sp.x <= l._bb.x + l._bb.w && sp.y >= l._bb.y && sp.y <= l._bb.y + l._bb.h)
      return { kind: 'labels', item: l };
  }
  // fixtures
  for (let i = state.doc.fixtures.length - 1; i >= 0; i--) {
    const f = state.doc.fixtures[i];
    const a = -rad(f.rot || 0), dx = p.x - f.x, dy = p.y - f.y;
    const lx = dx * Math.cos(a) - dy * Math.sin(a), ly = dx * Math.sin(a) + dy * Math.cos(a);
    const tol = 3 / z;
    if (Math.abs(lx) <= f.w / 2 + tol && Math.abs(ly) <= f.h / 2 + tol) return { kind: 'fixtures', item: f };
  }
  // openings
  for (let i = state.doc.openings.length - 1; i >= 0; i--) {
    const o = state.doc.openings[i];
    const wl = state.doc.walls.find(w => w.id === o.wallId); if (!wl) continue;
    const r = distToSeg(p.x, p.y, wl.x1, wl.y1, wl.x2, wl.y2);
    if (Math.abs(r.t - o.t) <= o.width / 2 && r.d <= wl.th / 2 + 6 / z) return { kind: 'openings', item: o };
  }
  // walls
  for (let i = state.doc.walls.length - 1; i >= 0; i--) {
    const w = state.doc.walls[i];
    const r = distToSeg(p.x, p.y, w.x1, w.y1, w.x2, w.y2);
    if (r.d <= w.th / 2 + 4 / z) return { kind: 'walls', item: w };
  }
  return null;
}

/** Handles for single-selected items; returns drag op or null. */
export function hitHandles(_p: Pt, sp: Pt): any {
  if (state.sel.size !== 1) return null;
  const r = findItem([...state.sel][0]); if (!r) return null;
  const z = state.view.zoom, v = state.view;
  const S = (wp: Pt) => ({ x: (wp.x - v.x) * z, y: (wp.y - v.y) * z });
  if (r.kind === 'walls') {
    const wl = r.item as Wall;
    for (const idx of [0, 1] as const) {
      const pt = idx ? { x: wl.x2, y: wl.y2 } : { x: wl.x1, y: wl.y1 };
      const s = S(pt);
      if (Math.hypot(sp.x - s.x, sp.y - s.y) < handleR()) return { kind: 'wallEnd', wall: wl, end: idx };
    }
  }
  if (r.kind === 'openings' && (r.item.type === 'door' || r.item.type === 'pocket')) {
    const o = r.item, wl = state.doc.walls.find(w => w.id === o.wallId);
    if (wl) for (const end of [0, 1] as const) {
      const s = S(wallPt(wl, o.t + (end ? o.width / 2 : -o.width / 2)));
      if (Math.hypot(sp.x - s.x, sp.y - s.y) < handleR()) return { kind: 'hinge', o, end };
    }
  }
  if (r.kind === 'fixtures') {
    const f = r.item, a = rad(f.rot || 0);
    const cs = Math.cos(a), sn = Math.sin(a);
    const local = (lx: number, ly: number) => ({ x: f.x + (lx * cs - ly * sn), y: f.y + (lx * sn + ly * cs) });
    const w2 = f.w / 2, h2 = f.h / 2;
    // rotate handle
    const rh = S(local(0, -h2 - 22 / z));
    if (Math.hypot(sp.x - rh.x, sp.y - rh.y) < handleR()) return { kind: 'rotate', f };
    const corners = [[-w2, -h2], [w2, -h2], [w2, h2], [-w2, h2]];
    for (let i = 0; i < 4; i++) {
      const s = S(local(corners[i][0], corners[i][1]));
      if (Math.hypot(sp.x - s.x, sp.y - s.y) < handleR() - 1) return { kind: 'resize', f, corner: i };
    }
  }
  return null;
}

/* =============================== pointer =============================== */
canvas.addEventListener('contextmenu', e => { e.preventDefault(); if (state.wallDraft) endWallChain(); });

canvas.addEventListener('pointerdown', e => {
  if (e.button === 2) return;
  pev('down');
  canvas.setPointerCapture(e.pointerId);
  const { sp, p } = evtPos(e);
  lastPointerType = e.pointerType;
  if (e.pointerType === 'touch') {
    touches.set(e.pointerId, sp);
    if (touches.size === 2) { startPinch(); return; }
    if (touches.size > 2) return;
  }
  if (e.button === 1 || state.spaceDown) {
    state.drag = { kind: 'pan', sx: sp.x, sy: sp.y, ox: state.view.x, oy: state.view.y };
    canvas.style.cursor = 'grabbing';
    return;
  }
  const t = state.tool;
  if (t === 'select') {
    // priority: drag handles of the selected item (endpoint clicks must drag,
    // never open a badge), then constraint badges, then dimension labels —
    // badge/dim editors are deferred so the mousedown's default focus action
    // doesn't blur the box
    const h0 = hitHandles(p, sp);
    if (!h0) {
      const tie = overTie(sp);
      if (tie) { e.preventDefault(); setTimeout(() => editConstraint(tie, sp), 0); return; }
      const dim = overDim(sp);
      if (dim) { e.preventDefault(); setTimeout(() => editWallDim(dim.wall, dim.which, sp), 0); return; }
    }
    const h = h0;
    if (h && h.kind === 'hinge') {
      // click the other jamb: the hinge moves there (clicking the hinge jamb does nothing)
      if ((h.end === 1) !== !!h.o.hinge) flipDoor('hinge');
      requestDraw(); return;
    }
    if (h) {
      pushUndo();
      if (h.kind === 'wallEnd') {
        const corner = h.end ? { x: h.wall.x2, y: h.wall.y2 } : { x: h.wall.x1, y: h.wall.y1 };
        const weld = collectWeld(state.doc.walls, [corner], new Set([h.wall.id]), false);
        state.drag = { kind: 'wallEnd', wall: h.wall, end: h.end, weld, start: corner,
                       lockLen: h.wall.locked ? wallVec(h.wall).len : 0 };
      } else if (h.kind === 'rotate') {
        state.drag = { kind: 'rotate', f: h.f };
      } else {
        const f = h.f, a = rad(f.rot || 0), cs = Math.cos(a), sn = Math.sin(a);
        const opp = [[1, 1], [-1, 1], [-1, -1], [1, -1]][h.corner]; // opposite corner sign
        const anchorL = { x: opp[0] * f.w / 2, y: opp[1] * f.h / 2 };
        state.drag = { kind: 'resize', f, cs, sn, anchorL,
          anchorW: { x: f.x + anchorL.x * cs - anchorL.y * sn, y: f.y + anchorL.x * sn + anchorL.y * cs } };
      }
      return;
    }
    const hit = hitTest(p, sp);
    if (hit) {
      if (e.shiftKey) {
        if (state.sel.has(hit.item.id)) state.sel.delete(hit.item.id);
        else state.sel.add(hit.item.id);
      } else if (!state.sel.has(hit.item.id)) {
        state.sel = new Set([hit.item.id]);
      }
      renderPanel();
      state.drag = { kind: 'maybeMove', start: p, sp, moved: false };
    } else {
      if (!e.shiftKey) { state.sel.clear(); renderPanel(); }
      state.drag = { kind: 'marquee', ax: p.x, ay: p.y, bx: p.x, by: p.y, add: e.shiftKey };
    }
    requestDraw();
  }
  else if (t === 'room') {
    const pt = e.altKey ? p : snapPt(p);
    state.roomDraft = { ax: pt.x, ay: pt.y, bx: pt.x, by: pt.y };
    state.drag = { kind: 'room' };
    requestDraw();
  }
  else if (t === 'constrain') {
    // wall-only picking (fixtures/openings never obscure a constraint click)
    let wl: Wall | null = null;
    for (let i = state.doc.walls.length - 1; i >= 0; i--) {
      const w = state.doc.walls[i];
      if (distToSeg(p.x, p.y, w.x1, w.y1, w.x2, w.y2).d <= w.th / 2 + 6 / state.view.zoom) { wl = w; break; }
    }
    if (wl) applyConstrainClick(wl, p);
    else { state.constrainFirst = null; state.sel.clear(); renderPanel(); }
    requestDraw();
  }
  else if (t === 'wall') {
    const pt = snapPt(p);
    if (!state.wallDraft) {
      const si = state.snapInd;
      state.wallDraft = { ax: pt.x, ay: pt.y, typed: '',
        startHost: (si?.type === 'edge' || si?.type === 'mid') && si.wall
          ? { id: si.wall.id, mid: si.type === 'mid' } : undefined };
    } else {
      commitWallSegment(p);
    }
    requestDraw();
  }
  else if (t in OPENING_DEFS) {
    const near = nearestWall(state.doc.walls, p, 14 / state.view.zoom);
    if (near) {
      const width = OPENING_DEFS[t as OpeningType].width;
      const v2 = wallVec(near.wall);
      if (v2.len < width + 2) return;
      pushUndo();
      const o: any = { id: uid('o'), type: t, wallId: near.wall.id,
                       t: clamp(snapV(near.t), width / 2, v2.len - width / 2), width };
      if (t === 'door') { o.hinge = 0; o.swing = distSide(near.wall, p) >= 0 ? 1 : -1; }
      if (t === 'double' || t === 'bifold') o.swing = distSide(near.wall, p) >= 0 ? 1 : -1;
      if (t === 'pocket') o.hinge = 0;
      state.doc.openings.push(o);
      state.sel = new Set([o.id]); setTool('select');
      markDirty(); renderPanel();
      if (t === 'door') hooks.coach('door');
    }
  }
  else if (t === 'fixture') {
    pushUndo();
    const gh = state.placeGhost!;
    const f = { id: uid('f'), type: state.fixtureType, x: gh.x!, y: gh.y!, w: gh.w, h: gh.h, rot: gh.rot, label: '' };
    state.doc.fixtures.push(f);
    state.sel = new Set([f.id]); setTool('select');
    markDirty(); renderPanel();
    hooks.coach('fixture');
  }
  else if (t === 'label') {
    if (!state.show.labels) setViewOpt('labels', true);   // placing a label while hidden would be baffling
    const pt = { x: snapV(p.x), y: snapV(p.y) };
    editNewLabel(pt, sp);
  }
  else if (t === 'measure') {
    state.measure = { ax: snapPt(p).x, ay: snapPt(p).y, bx: p.x, by: p.y };
    state.drag = { kind: 'measure' };
    requestDraw();
  }
});

/* A cancelled or capture-stolen pointer used to leave state.drag set with no
   more moves arriving — the drag looked frozen. Treat both like a pointerup. */
function endDragAbruptly(why: string): void {
  pev(why); perf.cancels++;
  if (!state.drag) return;
  if (['move', 'slideOpening', 'wallEnd', 'rotate', 'resize'].includes(state.drag.kind)) {
    markDirty(); renderPanel();
  }
  state.drag = null;
  state.snapInd = null; state.guides = [];
  canvas.style.cursor = 'default';
  requestDraw();
}
canvas.addEventListener('pointercancel', () => endDragAbruptly('pointercancel'));
canvas.addEventListener('lostpointercapture', () => { if (state.drag) endDragAbruptly('lostcapture'); });

canvas.addEventListener('pointermove', e => {
  const t0 = performance.now();
  pev('move');
  onPointerMove(e);
  perf.moveMs = performance.now() - t0;
  perf.moves++;
});
function onPointerMove(e: PointerEvent): void {
  const { sp, p } = evtPos(e);
  if (touches.has(e.pointerId)) touches.set(e.pointerId, sp);
  if (state.drag && state.drag.kind === 'pinch') { applyPinch(); return; }
  state.lastMouse = p;
  reportCursor(p.x, p.y);
  state.placePreview = p;
  state.snapInd = null; state.guides = [];   // re-set by whichever snap logic runs
  $('#coords').textContent = `${fmtLen(p.x)} , ${fmtLen(p.y)}`;
  const d = state.drag;
  if (d) {
    if (d.kind === 'pan') {
      state.view.x = d.ox - (sp.x - d.sx) / state.view.zoom;
      state.view.y = d.oy - (sp.y - d.sy) / state.view.zoom;
    }
    else if (d.kind === 'maybeMove') {
      if (Math.hypot(sp.x - d.sp.x, sp.y - d.sp.y) > 3) {
        pushUndo();
        state.drag = beginMove(d.start);
      }
    }
    else if (d.kind === 'move') applyMove(d, p, !e.altKey);
    else if (d.kind === 'slideOpening') applySlide(d, p);
    else if (d.kind === 'marquee') { d.bx = p.x; d.by = p.y; }
    else if (d.kind === 'room') {
      const rd = state.roomDraft!;
      let bx = p.x, by = p.y;
      if (!e.altKey) {
        const pt = snapPt(p);
        if (state.snapInd) { bx = pt.x; by = pt.y; }   // joined an existing corner/wall: exact
        else ({ bx, by } = snapRoomCorner(rd.ax, rd.ay, p.x, p.y, ROOM_TH, snapV));   // inside size on the grid
      }
      if (e.shiftKey) ({ bx, by } = squareDrag(rd.ax, rd.ay, bx, by));
      rd.bx = bx; rd.by = by;
    }
    else if (d.kind === 'wallEnd') {
      const excl = new Set<string>([d.wall.id, ...d.weld.map((w: WeldMove) => w.wall.id)]);
      const fixed = d.end ? { x: d.wall.x1, y: d.wall.y1 } : { x: d.wall.x2, y: d.wall.y2 };
      const np = wallEndSnap(p, fixed, excl, d.lockLen);
      // Fusion drag: constraints are hard — the frame lands on the nearest
      // legal spot, or (if there is none) doesn't land at all
      dragWallEnd(state.doc, d.wall, d.end, np, d.weld, d.start);
      clampOpenings(state.doc);
      markDirtySoft();
    }
    else if (d.kind === 'rotate') {
      const a = deg(Math.atan2(p.y - d.f.y, p.x - d.f.x)) + 90;
      d.f.rot = Math.round(a / 15) * 15 % 360;
      markDirtySoft();
    }
    else if (d.kind === 'resize') {
      const f = d.f;
      const dx = p.x - d.anchorW.x, dy = p.y - d.anchorW.y;
      let lx = dx * d.cs + dy * d.sn, ly = -dx * d.sn + dy * d.cs;
      const nw = Math.max(3, Math.abs(snapV(lx))), nh = Math.max(3, Math.abs(snapV(ly)));
      lx = (lx < 0 ? -1 : 1) * nw; ly = (ly < 0 ? -1 : 1) * nh;
      f.w = nw; f.h = nh;
      // center = anchor + half-diagonal in local frame
      const hx = lx / 2, hy = ly / 2;
      f.x = d.anchorW.x + hx * d.cs - hy * d.sn;
      f.y = d.anchorW.y + hx * d.sn + hy * d.cs;
      markDirtySoft();
    }
    else if (d.kind === 'measure') {
      const sn = keys.alt ? p : snapPt(p);
      state.measure!.bx = sn.x; state.measure!.by = sn.y;
    }
    // keep the panel's numbers (length, clear, size, rotation…) live while dragging
    if (['wallEnd', 'move', 'resize', 'rotate', 'slideOpening'].includes(d.kind) &&
        performance.now() - lastPanelRefresh > 100) {
      lastPanelRefresh = performance.now();
      renderPanel();
    }
    requestDraw();
    return;
  }
  // hover + fixture ghost
  if (state.tool === 'select') {
    const hit = hitTest(p, sp);
    const nh = hit ? hit.item.id : null;
    if (nh !== state.hover) { state.hover = nh; requestDraw(); }
    const overLbl = overTie(sp) || overDim(sp);
    canvas.style.cursor = (overLbl || hitHandles(p, sp)) ? 'pointer' : (nh ? 'move' : 'default');
  }
  if (state.tool === 'fixture') {
    const def = FIXTURES[state.fixtureType];
    if (!state.placeGhost) state.placeGhost = { w: def.w, h: def.h, rot: 0 };
    const gh = state.placeGhost;
    const pose = e.altKey ? null : snapFixtureToWall(state.fixtureType, gh, p, state.doc.walls, snapV);
    if (pose) { gh.x = pose.x; gh.y = pose.y; gh.rot = pose.rot; state.guides.push({ kind: 'face', wall: pose.wall }); }
    else { gh.x = snapV(p.x); gh.y = snapV(p.y); }
    requestDraw();
  }
  if (state.tool === 'wall' || state.tool in OPENING_DEFS) requestDraw();
}

const pointInPoly = (p: Pt, poly: Pt[]): boolean => {
  let on = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i], b = poly[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < (b.x - a.x) * (p.y - a.y) / (b.y - a.y) + a.x) on = !on;
  }
  return on;
};

function endTouch(e: PointerEvent): boolean {   // true = this was a pinch finger lifting
  touches.delete(e.pointerId);
  if (state.drag && state.drag.kind === 'pinch') {
    if (touches.size < 2) { state.drag = null; requestDraw(); }
    return true;
  }
  return false;
}
canvas.addEventListener('pointercancel', e => { if (!endTouch(e)) { state.drag = null; state.roomDraft = null; hideLenBox(); requestDraw(); } });

canvas.addEventListener('pointerup', e => {
  pev('up');
  if (endTouch(e)) return;
  const d = state.drag;
  if (!d) return;
  if (d.kind === 'pan') canvas.style.cursor = state.spaceDown ? 'grab' : 'default';
  if (d.kind === 'marquee') {
    selectMarquee(d);
  }
  if (d.kind === 'room') {
    const rd = state.roomDraft; state.roomDraft = null; hideLenBox();
    if (rd) addRoom(normRect(rd.ax, rd.ay, rd.bx, rd.by));   // too small → stays in the Room tool for another go
  }
  if (d.kind === 'maybeMove') {
    // click without drag: plain click on selected item with no shift re-selects only it
    if (!e.shiftKey) {
      const { sp, p } = evtPos(e);
      const hit = hitTest(p, sp);
      if (hit) state.sel = new Set([hit.item.id]);
      renderPanel();
    }
  }
  // dropping a wall end ONTO another wall's span (or its midpoint) pins it
  // there, Fusion-style — and the landing direction becomes a constraint too
  // (exact ⊥ from the foot-snap ⇒ axis or 90°); badges delete them again
  if (d.kind === 'wallEnd' && !keys.alt &&
      (state.snapInd?.type === 'edge' || state.snapInd?.type === 'mid')) {
    const host = state.snapInd.wall;
    if (host && host.id !== d.wall.id) {
      const cs = (state.doc.constraints ||= []);
      if (!cs.some(c => (c.type === 'coin' || c.type === 'mid') && c.a === d.wall.id && c.end === d.end))
        cs.push({ id: uid('c'), type: 'coin', a: d.wall.id, end: d.end, b: host.id });
      cs.push(...inferDropConstraints(state.doc, d.wall, host));
    }
  }
  if (['move', 'slideOpening', 'wallEnd', 'rotate', 'resize'].includes(d.kind)) { markDirty(); renderPanel(); }
  state.drag = null;
  state.snapInd = null; state.guides = [];
  requestDraw();
});

function beginMove(start: Pt): any {
  const ids = state.sel;
  // grounded (📌 fixed) walls never join a move — Fusion leaves them put
  const fixedIds = new Set((state.doc.constraints || []).filter(c => c.type === 'fix').map(c => c.a));
  const walls: any[] = [], fixtures: any[] = [], labels: any[] = [], openings: any[] = [];
  for (const id of ids) {
    const r = findItem(id); if (!r) continue;
    if (r.kind === 'walls' && fixedIds.has(id)) continue;
    if (r.kind === 'walls') walls.push({ w: r.item, x1: r.item.x1, y1: r.item.y1, x2: r.item.x2, y2: r.item.y2 });
    if (r.kind === 'fixtures') fixtures.push({ f: r.item, x: r.item.x, y: r.item.y });
    if (r.kind === 'labels') labels.push({ l: r.item, x: r.item.x, y: r.item.y });
    if (r.kind === 'openings') openings.push(r.item);
  }
  // sole opening selected → slide along its wall
  if (openings.length === 1 && !walls.length && !fixtures.length && !labels.length) {
    return { kind: 'slideOpening', o: openings[0] };
  }
  // welded endpoints of unselected walls (locked walls translate rigidly)
  const selWallIds = new Set<string>(walls.map(x => x.w.id));
  const pts: Pt[] = [];
  for (const rec of walls) pts.push({ x: rec.x1, y: rec.y1 }, { x: rec.x2, y: rec.y2 });
  const weld = collectWeld(state.doc.walls, pts, selWallIds, false);
  return { kind: 'move', start, walls, fixtures, labels, weld };
}
function applyMove(d: any, p: Pt, magnet = true): void {
  // ⌥ frees the grid while moving, matching the endpoint-drag behavior
  const ddx = keys.alt ? p.x - d.start.x : snapV(p.x - d.start.x);
  const ddy = keys.alt ? p.y - d.start.y : snapV(p.y - d.start.y);
  const snap = d.walls.length ? snapshotWalls(state.doc) : null;
  for (const r of d.walls) { r.w.x1 = r.x1 + ddx; r.w.y1 = r.y1 + ddy; r.w.x2 = r.x2 + ddx; r.w.y2 = r.y2 + ddy; }
  for (const r of d.fixtures) { r.f.x = r.x + ddx; r.f.y = r.y + ddy; }
  // one piece of furniture on its own is magnetic: flush to a nearby wall, back to it (⌥ to carry it free)
  if (magnet && d.fixtures.length === 1 && !d.walls.length && !d.labels.length) {
    const f = d.fixtures[0].f;
    const pose = snapFixtureToWall(f.type, f, { x: f.x, y: f.y }, state.doc.walls, snapV);
    if (pose) { f.x = pose.x; f.y = pose.y; f.rot = pose.rot; state.guides.push({ kind: 'face', wall: pose.wall }); }
  }
  for (const r of d.labels) { r.l.x = r.x + ddx; r.l.y = r.y + ddy; }
  for (const r of d.weld) {
    if (r.end) { r.wall.x2 = r.x + ddx; r.wall.y2 = r.y + ddy; } else { r.wall.x1 = r.x + ddx; r.wall.y1 = r.y + ddy; }
  }
  const movedIds = d.walls.map((r: any) => r.w.id);
  // hold the moved walls; if their own constraints forbid that exact spot,
  // let the constraints steer them; a truly impossible frame is rejected
  if (snap &&
      !solveConstraints(state.doc, new Set(movedIds), movedIds).ok &&
      !solveConstraints(state.doc, new Set(), movedIds).ok)
    restoreWalls(snap);
  clampOpenings(state.doc);
  markDirtySoft();
}
function applySlide(d: any, p: Pt): void {
  const o = d.o;
  const wl = state.doc.walls.find(w => w.id === o.wallId); if (!wl) return;
  const r = distToSeg(p.x, p.y, wl.x1, wl.y1, wl.x2, wl.y2);
  const v2 = wallVec(wl);
  o.t = clamp(snapV(r.t), o.width / 2, Math.max(o.width / 2, v2.len - o.width / 2));
  // carried clearly to one side of the wall: that's the side it should open to
  if (o.type === 'door' || o.type === 'double' || o.type === 'bifold') {
    const side = distSide(wl, p);
    if (Math.abs(side) > wl.th / 2 + 2) o.swing = side >= 0 ? 1 : -1;
  }
  markDirtySoft();
}
function selectMarquee(d: any): void {
  const x1 = Math.min(d.ax, d.bx), y1 = Math.min(d.ay, d.by), x2 = Math.max(d.ax, d.bx), y2 = Math.max(d.ay, d.by);
  if (!d.add) state.sel.clear();
  for (const w of state.doc.walls)
    if (segIntersectsRect(w.x1, w.y1, w.x2, w.y2, x1, y1, x2, y2)) state.sel.add(w.id);
  for (const f of state.doc.fixtures)
    if (f.x >= x1 && f.x <= x2 && f.y >= y1 && f.y <= y2) state.sel.add(f.id);
  if (state.show.labels) for (const l of state.doc.labels)
    if (l.x >= x1 && l.x <= x2 && l.y >= y1 && l.y <= y2) state.sel.add(l.id);
  for (const o of state.doc.openings) {
    const wl = state.doc.walls.find(w => w.id === o.wallId); if (!wl) continue;
    const c = wallPt(wl, o.t);
    if (c.x >= x1 && c.x <= x2 && c.y >= y1 && c.y <= y2) state.sel.add(o.id);
  }
  renderPanel();
}

function commitWallSegment(mouse: Pt): void {
  const d = state.wallDraft!;
  const end = wallDraftEnd(d, mouse);
  const len = Math.hypot(end.x - d.ax, end.y - d.ay);
  if (len < 1) { return; }
  pushUndo();
  const w: Wall = { id: uid('w'), x1: d.ax, y1: d.ay, x2: end.x, y2: end.y, th: 4.5 };
  state.doc.walls.push(w);
  // Fusion-style: the snap that guided the draw becomes a real constraint
  // (H/V axis, ⊥/∥ to the reference wall, ⚯ tie onto an edge-snapped host);
  // ⌥ draws free with none
  if (!keys.alt)
    (state.doc.constraints ||= []).push(
      ...inferDrawConstraints(w, end.ref ? { id: end.ref.id, rel: end.rel! } : null,
        { start: d.startHost,
          end: end.mid ? { id: end.mid.id, mid: true } : end.edge ? { id: end.edge.id } : undefined }));
  state.wallDraft = { ax: end.x, ay: end.y, typed: '' };
  markDirty();
}
export function endWallChain(): void { state.wallDraft = null; hideLenBox(); requestDraw(); }

canvas.addEventListener('dblclick', e => {
  const { sp, p } = evtPos(e);
  if (state.tool === 'wall') { endWallChain(); return; }
  if (state.tool !== 'select') return;
  const hit = hitTest(p, sp);
  if (hit && hit.kind === 'labels') editLabel(hit.item, sp);
  else if (hit && hit.kind === 'fixtures') editFixtureLabel(hit.item, sp);
  else if (!hit) {
    // inside an enclosed room: name it (label lands at the room's label spot); elsewhere: zoom to fit
    const room = state.rooms.find(r => p.x >= r.x1 && p.x <= r.x2 && p.y >= r.y1 && p.y <= r.y2 && pointInPoly(p, r.pts));
    if (room) {
      if (!state.show.labels) setViewOpt('labels', true);
      const lp = { x: snapV(room.cx), y: snapV(room.cy) };
      const v = state.view;
      editNewLabel(lp, { x: (lp.x - v.x) * v.zoom, y: (lp.y - v.y) * v.zoom });
    } else fitView();
  }
});

/* wheel: pan; ctrl/cmd or pinch: zoom */
canvas.addEventListener('wheel', e => {
  e.preventDefault();
  const { sp } = evtPos(e);
  if (e.ctrlKey || e.metaKey) {
    const factor = Math.exp(-e.deltaY * 0.012);
    zoomAt(sp, clamp(state.view.zoom * factor, .25, 60));
  } else {
    state.view.x += e.deltaX / state.view.zoom;
    state.view.y += e.deltaY / state.view.zoom;
    requestDraw();
  }
}, { passive: false });

/* =============================== keyboard =============================== */
window.addEventListener('keydown', e => {
  keys.alt = e.altKey; keys.shift = e.shiftKey;
  const tag = (e.target as HTMLElement).tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
  const mod = e.metaKey || e.ctrlKey;
  if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? redo() : undo(); requestDraw(); return; }
  if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); redo(); requestDraw(); return; }
  if (mod && e.key.toLowerCase() === 'd') { e.preventDefault(); duplicateSel(); return; }
  if (mod && e.key.toLowerCase() === 'a') { e.preventDefault();
    state.sel = allIds(); renderPanel(); requestDraw(); return; }
  if (mod && e.key.toLowerCase() === 's') { e.preventDefault(); saveNow(); return; }
  if (mod) return;
  if (e.key === ' ') { e.preventDefault(); state.spaceDown = true; canvas.style.cursor = 'grab'; return; }
  // typed length during wall draw
  if (state.tool === 'wall' && state.wallDraft) {
    if (/^[\d.'"\/ ]$/.test(e.key)) { state.wallDraft.typed += e.key; requestDraw(); return; }
    if (e.key === 'Backspace') { state.wallDraft.typed = state.wallDraft.typed.slice(0, -1); requestDraw(); return; }
    if (e.key === 'Enter') { commitWallSegment(state.lastMouse); requestDraw(); return; }
  }
  switch (e.key) {
    case 'Escape':
      if ($('#helpModal').classList.contains('show')) { $('#helpModal').classList.remove('show'); break; }
      if (state.roomDraft) { state.roomDraft = null; state.drag = null; hideLenBox(); requestDraw(); }
      else if (state.wallDraft) endWallChain();
      else if (state.measure) { state.measure = null; requestDraw(); }
      else if (state.tool !== 'select') setTool('select');
      else { state.sel.clear(); renderPanel(); requestDraw(); }
      break;
    case 'Delete': case 'Backspace': deleteSel(); break;
    case 'v': case 'V': setTool('select'); break;
    case 'a': case 'A': setTool('room'); break;
    case 'w': case 'W': setTool('wall'); break;
    case 'c': case 'C': setTool('constrain'); break;
    case 'd': case 'D': setTool('door'); break;
    case 'u': case 'U': setTool('double'); break;
    case 'i': case 'I': setTool('bifold'); break;
    case 'b': case 'B': setTool('slider'); break;
    case 'p': case 'P': setTool('pocket'); break;
    case 'o': case 'O': setTool('cased'); break;
    case 'n': case 'N': setTool('window'); break;
    case 'x': case 'X': setTool('fixture'); break;
    case 't': case 'T': setTool('label'); break;
    case 'm': case 'M': setTool('measure'); break;
    case 'f': case 'F': fitView(); break;
    case 'r': case 'R': rotateSel(e.shiftKey ? -90 : 90); break;
    case '?': $('#helpModal').classList.toggle('show'); break;
    case 'h': case 'H': flipDoor('hinge'); break;
    case 's': case 'S': flipDoor('swing'); break;
    case '=': case '+': zoomAt({ x: CSS_W / 2, y: CSS_H / 2 }, clamp(state.view.zoom * 1.25, .25, 60)); break;
    case '-': case '_': zoomAt({ x: CSS_W / 2, y: CSS_H / 2 }, clamp(state.view.zoom / 1.25, .25, 60)); break;
    case 'ArrowLeft': nudge(-1, 0, e.shiftKey); e.preventDefault(); break;
    case 'ArrowRight': nudge(1, 0, e.shiftKey); e.preventDefault(); break;
    case 'ArrowUp': nudge(0, -1, e.shiftKey); e.preventDefault(); break;
    case 'ArrowDown': nudge(0, 1, e.shiftKey); e.preventDefault(); break;
  }
});
window.addEventListener('keyup', e => {
  keys.alt = e.altKey; keys.shift = e.shiftKey;
  if (e.key === ' ') { state.spaceDown = false; canvas.style.cursor = 'default'; }
});
window.addEventListener('blur', () => { state.spaceDown = false; });

let lastNudge = 0;
function nudge(dx: number, dy: number, big: boolean): void {
  if (!state.sel.size) return;
  if (performance.now() - lastNudge > 900) pushUndo();
  lastNudge = performance.now();
  const step = big ? 12 : 1;
  const d = beginMove({ x: 0, y: 0 });
  if (d.kind === 'slideOpening') {
    const o = d.o, wl = state.doc.walls.find(w => w.id === o.wallId);
    if (wl) { const v2 = wallVec(wl); o.t = clamp(o.t + (dx + dy) * step, o.width / 2, v2.len - o.width / 2); }
  } else {
    applyMove({ ...d, start: { x: 0, y: 0 } }, { x: dx * step, y: dy * step }, false);   // nudging is deliberate: no magnet
  }
  markDirty(); renderPanel();
}
